import { useEffect, useMemo, useRef, useState } from "react";
import useAuthAccess from "../hooks/useAuthAccess";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import {
  Bookmark,
  Heart,
  Filter,
  X,
  Search,
  RefreshCw,
  ShieldCheck,
  ArrowUpDown,
  Crown,
  Lock,
  ChevronRight,
} from "lucide-react";
import {
  arrayUnion,
  arrayRemove,
  collection,
  doc,
  getDoc,
  getDocs,
  getCountFromServer,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
} from "firebase/firestore";
import Navbar from "../Landing/Navbar";
import { auth, db } from "../firebase";
import { filterVisibleProfiles } from "../utils/profileVisibility";
import { findProfileForUser } from "../utils/profileLookup";
import { isVerifiedProfile } from "../utils/verification";
import { isPremiumActive } from "../utils/membership";
import ProfileCard from "../Components/ProfileCard";
import EmptyState from "../Components/EmptyState";
import ConfirmDialog from "../Components/ConfirmDialog";
import Combobox from "../Components/Combobox";
import InfoModal from "../Components/InfoModal";
import { compareProfilesBySortOrder } from "../utils/profileSort";
import { ProfileCardGridSkeleton } from "../Components/Skeleton";
import { indiaAddressOptions, indiaStates } from "../data/addressOptions";

const PAGE_SIZE = 12;
const YEAR_MIN = new Date().getFullYear() - 60;
const YEAR_MAX = new Date().getFullYear() - 18;
const MATCHES_PAGE_STORAGE_KEY = "karankiMatchesPageState";

const loadMatchesPageState = (userId) => {
  if (!userId) return null;
  try {
    const raw = sessionStorage.getItem(MATCHES_PAGE_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data.userId === userId ? data : null;
  } catch {
    return null;
  }
};

const saveMatchesPageState = (payload) => {
  try {
    sessionStorage.setItem(MATCHES_PAGE_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore session storage failures
  }
};

const yearOptions = Array.from(
  { length: 43 },
  (_, index) => String(new Date().getFullYear() - 18 - index)
);
const partnerEducationOptions = [
  "10th", "Intermediate / 12th", "Diploma", "ITI",
  "B.A", "B.Com", "B.Sc", "B.Tech / B.E",
  "BBA", "BCA", "LLB", "CA", "CS", "MBBS", "BDS", "B.Pharmacy", "BVSC",
  "M.A", "M.Com", "M.Sc", "M.Tech / M.E", "MBA", "MCA", "MVSC",
  "MD / MS", "Ph.D", "Any",
];
const partnerProfessionOptions = [
  "Software Engineer", "Doctor", "Nurse", "Pharmacist", "Engineer",
  "Teacher / Lecturer", "Professor", "Government Employee", "Banking Professional", "Accountant",
  "Chartered Accountant", "Business Owner", "Self Employed", "Farmer / Agriculture", "Lawyer",
  "Police", "Defence Personnel", "Civil Services", "Sales / Marketing",
  "Designer", "Architect", "Consultant", "Entrepreneur", "Student", "Homemaker", "Not Working", "Any",
];
const partnerCasteOptions = ["Goud / Gowda", "Settibalija", "Ediga", "Srisayana", "Yatha", "Any"];

// Premium advanced filter options — height as inches for slider (54 = 4'6" to 78 = 6'6")
const HEIGHT_MIN_INCHES = 54;
const HEIGHT_MAX_INCHES = 78;
const inchesToLabel = (inches) => `${Math.floor(inches / 12)}'${inches % 12}"`;
const labelToInches = (label) => {
  if (!label) return 0;
  const match = label.match(/(\d+)'(\d+)"/);
  if (!match) return 0;
  return Number(match[1]) * 12 + Number(match[2]);
};
const heightOptions = Array.from({ length: HEIGHT_MAX_INCHES - HEIGHT_MIN_INCHES + 1 }, (_, i) => inchesToLabel(HEIGHT_MIN_INCHES + i));
const maritalStatusOptions = ["Never Married", "Divorced", "Widowed", "Separated", "Any"];
const religionOptions = ["Hindu", "Converted Christian", "Any"];
const incomeOptions = ["Not Working", "Below 2 Lakhs", "2-5 Lakhs", "5-10 Lakhs", "10-15 Lakhs", "15-25 Lakhs", "25-50 Lakhs", "50 Lakhs - 1 Crore", "Above 1 Crore", "Prefer not to say", "Any"];
const residenceTypeOptions = ["India", "NRI", "Any"];
const rashiOptions = ["Mesha (మేషం)", "Vrishabha (వృషభం)", "Mithuna (మిథునం)", "Karka (కర్కాటకం)", "Simha (సింహం)", "Kanya (కన్య)", "Tula (తుల)", "Vrischika (వృశ్చికం)", "Dhanu (ధనుస్సు)", "Makara (మకరం)", "Kumbha (కుంభం)", "Meena (మీనం)", "Any"];
const familyFinancialOptions = ["Lower Middle Class", "Middle Class", "Upper Middle Class", "Rich", "Prefer Not to Say", "Any"];
const collegeTypeOptions = ["IIT", "NIT", "IIIT", "IIM", "AIIMS", "BITS", "Government University", "Government College", "Private University", "Private College", "Deemed University", "Foreign University"];

const formatDateOfBirth = (d) => {
  if (!d) return "DOB not added";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "DOB not added";
  return date.toLocaleDateString("en-GB");
};

const getBirthYear = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  return String(date.getFullYear());
};

const normalizePreference = (value) => {
  const normalized = String(value || "").trim();
  return normalized && normalized.toLowerCase() !== "any" ? normalized : "";
};

const getOppositeGender = (g) => (g === "Male" ? "Female" : g === "Female" ? "Male" : "");

function buildMatchesQuery(targetGender, lastVisibleDoc = null) {
  const constraints = [
    where("gender", "==", targetGender),
    where("profileStatus", "==", "Verified"),
    orderBy("createdAt", "desc"),
  ];
  if (lastVisibleDoc) {
    constraints.push(startAfter(lastVisibleDoc));
  }
  constraints.push(limit(PAGE_SIZE));
  return query(collection(db, "profiles"), ...constraints);
}

async function fetchUserDoc(userId) {
  const snap = await getDoc(doc(db, "users", userId));
  return snap.exists() ? snap.data() : { blocked: [], shortlisted: [] };
}

async function fetchMyProfile(user) {
  const savedPhone = JSON.parse(localStorage.getItem("karankiAuth") || "null")?.phone
    || localStorage.getItem("karankiProfilePhone");
  return await findProfileForUser(db, user, savedPhone);
}

async function fetchMatchesPage({ queryKey, pageParam = null }) {
  const [, targetGender, blockedIds, userId, viewerIsPremium = false] = queryKey;
  const snapshot = await getDocs(buildMatchesQuery(targetGender, pageParam));
  const docs = snapshot.docs;
  const fetched = docs.map((it) => ({ id: it.id, ...it.data() }));

  const visible = filterVisibleProfiles(
    fetched.filter((it) => it.id !== userId),
    blockedIds || [],
    viewerIsPremium
  );

  return {
    profiles: visible,
    nextCursor: docs.length === PAGE_SIZE ? docs[docs.length - 1] : null,
  };
}

/* =================================================================
   Filter form
================================================================= */
function BasicFilters({ filters, setFilters, educationOptions, professionOptions, casteOptions }) {
  return (
    <div className="mx-auto w-full max-w-2xl space-y-5">
      <div>
        <h3 className="font-display text-base font-bold text-ink-800">Basic preferences</h3>
        <p className="mt-1 text-[12px] text-ink-400">Choose the details that matter most to you.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-sand-200 bg-white p-4 sm:col-span-2">
          <label className="field-label">Birth year range</label>
          <div className="mt-2 rounded-xl bg-sand-50 p-4">
            <div className="flex items-center justify-between text-[13px] font-semibold text-ink-800">
              <span>{filters.dobFrom || YEAR_MIN}</span>
              <span className="text-[11px] text-ink-400">to</span>
              <span>{filters.dobTo || YEAR_MAX}</span>
            </div>
            <div className="relative mt-3 flex items-center gap-3">
              <input
                type="range"
                min={YEAR_MIN}
                max={YEAR_MAX}
                value={filters.dobFrom || YEAR_MIN}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  const maxVal = filters.dobTo ? Number(filters.dobTo) : YEAR_MAX;
                  setFilters({ ...filters, dobFrom: val > YEAR_MIN ? String(Math.min(val, maxVal)) : "" });
                }}
                className="h-2 w-full cursor-pointer appearance-none rounded-full bg-sand-200 accent-brand-600 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-600 [&::-webkit-slider-thumb]:shadow-soft"
              />
              <input
                type="range"
                min={YEAR_MIN}
                max={YEAR_MAX}
                value={filters.dobTo || YEAR_MAX}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  const minVal = filters.dobFrom ? Number(filters.dobFrom) : YEAR_MIN;
                  setFilters({ ...filters, dobTo: val < YEAR_MAX ? String(Math.max(val, minVal)) : "" });
                }}
                className="h-2 w-full cursor-pointer appearance-none rounded-full bg-sand-200 accent-brand-600 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-600 [&::-webkit-slider-thumb]:shadow-soft"
              />
            </div>
            {(filters.dobFrom || filters.dobTo) && (
              <button
                type="button"
                onClick={() => setFilters({ ...filters, dobFrom: "", dobTo: "" })}
                className="mt-2 text-[11px] font-semibold text-brand-600 hover:underline"
              >
                Clear year filter
              </button>
            )}
          </div>
        </div>
        <div className="min-w-0">
          <label className="field-label">Education</label>
          <Combobox value={filters.education} onChange={(v) => setFilters({ ...filters, education: v })} options={educationOptions} placeholder="Any" searchPlaceholder="Search education..." />
        </div>
        <div className="min-w-0">
          <label className="field-label">Profession</label>
          <Combobox value={filters.profession} onChange={(v) => setFilters({ ...filters, profession: v })} options={professionOptions} placeholder="Any" searchPlaceholder="Search profession..." />
        </div>
        <div className="min-w-0 sm:col-span-2">
          <label className="field-label">Caste preference</label>
          <Combobox value={filters.caste} onChange={(v) => setFilters({ ...filters, caste: v })} options={casteOptions} placeholder="Any" searchPlaceholder="Search caste..." />
        </div>
      </div>
    </div>
  );
}

function AdvancedFilters({ filters, setFilters }) {
  const residenceType = filters.residenceType || "";
  const isIndia = residenceType === "India";
  const districtOptions = isIndia && filters.state && filters.state !== "Any"
    ? [...Object.keys(indiaAddressOptions[filters.state] || {}), "Any"]
    : ["Any"];

  return (
    <div className="mx-auto grid w-full max-w-2xl gap-5 sm:grid-cols-2">
      <div className="rounded-2xl border border-sand-200 bg-white p-4 sm:col-span-2">
        <label className="field-label">Height range</label>
        <div className="mt-2 rounded-xl bg-sand-50 p-4">
          <div className="flex items-center justify-between text-[13px] font-semibold text-ink-800">
            <span>{filters.heightMin || inchesToLabel(HEIGHT_MIN_INCHES)}</span>
            <span className="text-[11px] text-ink-400">to</span>
            <span>{filters.heightMax || inchesToLabel(HEIGHT_MAX_INCHES)}</span>
          </div>
          <div className="relative mt-3 flex items-center gap-3">
            <input
              type="range"
              min={HEIGHT_MIN_INCHES}
              max={HEIGHT_MAX_INCHES}
              value={filters.heightMin ? labelToInches(filters.heightMin) : HEIGHT_MIN_INCHES}
              onChange={(e) => {
                const val = Number(e.target.value);
                const maxVal = filters.heightMax ? labelToInches(filters.heightMax) : HEIGHT_MAX_INCHES;
                setFilters({ ...filters, heightMin: val > HEIGHT_MIN_INCHES ? inchesToLabel(Math.min(val, maxVal)) : "" });
              }}
              className="h-2 w-full cursor-pointer appearance-none rounded-full bg-sand-200 accent-brand-600 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-600 [&::-webkit-slider-thumb]:shadow-soft"
            />
            <input
              type="range"
              min={HEIGHT_MIN_INCHES}
              max={HEIGHT_MAX_INCHES}
              value={filters.heightMax ? labelToInches(filters.heightMax) : HEIGHT_MAX_INCHES}
              onChange={(e) => {
                const val = Number(e.target.value);
                const minVal = filters.heightMin ? labelToInches(filters.heightMin) : HEIGHT_MIN_INCHES;
                setFilters({ ...filters, heightMax: val < HEIGHT_MAX_INCHES ? inchesToLabel(Math.max(val, minVal)) : "" });
              }}
              className="h-2 w-full cursor-pointer appearance-none rounded-full bg-sand-200 accent-brand-600 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-600 [&::-webkit-slider-thumb]:shadow-soft"
            />
          </div>
          {(filters.heightMin || filters.heightMax) && (
            <button
              type="button"
              onClick={() => setFilters({ ...filters, heightMin: "", heightMax: "" })}
              className="mt-2 text-[11px] font-semibold text-brand-600 hover:underline"
            >
              Clear height filter
            </button>
          )}
        </div>
      </div>
      <div className="min-w-0">
        <label className="field-label">Marital status</label>
        <Combobox value={filters.maritalStatus} onChange={(v) => setFilters({ ...filters, maritalStatus: v })} options={maritalStatusOptions} placeholder="Any" searchPlaceholder="Search status..." />
      </div>
      <div className="min-w-0">
        <label className="field-label">Religion</label>
        <Combobox value={filters.religion} onChange={(v) => setFilters({ ...filters, religion: v })} options={religionOptions} placeholder="Any" searchPlaceholder="Search religion..." />
      </div>
      <div className="min-w-0">
        <label className="field-label">Rashi</label>
        <Combobox value={filters.rashi} onChange={(v) => setFilters({ ...filters, rashi: v })} options={rashiOptions} placeholder="Any" searchPlaceholder="Search rashi..." />
      </div>
      <div className="min-w-0">
        <label className="field-label">Annual income</label>
        <Combobox value={filters.income} onChange={(v) => setFilters({ ...filters, income: v })} options={incomeOptions} placeholder="Any" searchPlaceholder="Search income..." />
      </div>
      <div className="min-w-0">
        <label className="field-label">Residence type</label>
        <Combobox
          value={filters.residenceType}
          onChange={(v) => setFilters({ ...filters, residenceType: v, state: "", district: "" })}
          options={residenceTypeOptions}
          placeholder="Any"
          searchPlaceholder="Search type..."
        />
      </div>
      {isIndia && (
        <>
          <div>
            <label className="field-label">State</label>
          <Combobox
            value={filters.state}
            onChange={(v) => setFilters({ ...filters, state: v, district: "" })}
            options={[...indiaStates, "Any"]}
            placeholder="Any"
            searchPlaceholder="Search state..."
          />
          </div>
          <div>
            <label className="field-label">District</label>
            <Combobox
              value={filters.district}
              onChange={(v) => setFilters({ ...filters, district: v })}
              options={districtOptions}
              placeholder={filters.state && filters.state !== "Any" ? "Any" : "Select state first"}
              searchPlaceholder="Search district..."
              disabled={!filters.state || filters.state === "Any"}
            />
          </div>
        </>
      )}
      <div>
        <label className="field-label">Family status</label>
        <Combobox value={filters.familyFinancialStatus} onChange={(v) => setFilters({ ...filters, familyFinancialStatus: v })} options={familyFinancialOptions} placeholder="Any" searchPlaceholder="Search status..." />
      </div>
      <div className="sm:col-span-2">
        <label className="field-label">College type <span className="text-ink-400 font-normal">(select multiple)</span></label>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {collegeTypeOptions.map((type) => {
            const selected = filters.collegeTypes.includes(type);
            return (
              <button
                key={type}
                type="button"
                onClick={() => {
                  const next = selected
                    ? filters.collegeTypes.filter((t) => t !== type)
                    : [...filters.collegeTypes, type];
                  setFilters({ ...filters, collegeTypes: next });
                }}
                className={`rounded-full px-3 py-1.5 text-[12px] font-semibold transition ${
                  selected
                    ? "bg-brand-gradient text-white shadow-soft"
                    : "border border-sand-200 bg-white text-ink-600 hover:border-brand-600 hover:text-brand-600"
                }`}
              >
                {type}
              </button>
            );
          })}
        </div>
        {filters.collegeTypes.length > 0 && (
          <button
            type="button"
            onClick={() => setFilters({ ...filters, collegeTypes: [] })}
            className="mt-2 text-[11px] font-semibold text-brand-600 hover:underline"
          >
            Clear selection
          </button>
        )}
      </div>
      <label className="card flex items-center justify-between gap-3 p-3 sm:col-span-2 cursor-pointer">
        <div>
          <p className="text-[13px] font-semibold text-ink-800">Show only profiles with photos</p>
          <p className="text-[11.5px] text-ink-400">Skip profiles that don't have a photo</p>
        </div>
        <input
          type="checkbox"
          checked={filters.withPhotosOnly}
          onChange={(e) => setFilters({ ...filters, withPhotosOnly: e.target.checked })}
          className="h-5 w-5 accent-brand-600"
        />
      </label>
    </div>
  );
}

/* =================================================================
   Main
================================================================= */
const Matches = () => {
  const { user } = useAuthAccess();
  const [filters, setFilters] = useState({
    dobFrom: "", dobTo: "",
    education: "", profession: "", caste: "",
    // Advanced (premium)
    heightMin: "", heightMax: "",
    maritalStatus: "", religion: "",
    income: "", residenceType: "",
    state: "", district: "", rashi: "",
    familyFinancialStatus: "",
    collegeTypes: [],
    withPhotosOnly: false,
  });
  const [search, setSearch] = useState("");
  const [notice, setNotice] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);
  const [filterTab, setFilterTab] = useState("basic"); // 'basic' | 'advanced'
  const [blockedIds, setBlockedIds] = useState([]);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [shortlistBusyIds, setShortlistBusyIds] = useState([]);
  const [shortlistedIds, setShortlistedIds] = useState([]);
  const [pendingUnshortlist, setPendingUnshortlist] = useState(null);

  const sentinelRef = useRef(null);
  const restoredState = useRef(false);
  const matchesScrollKey = "karankiMatchesScroll";

  const saveMatchesScroll = () => {
    try {
      sessionStorage.setItem(matchesScrollKey, String(window.scrollY || 0));
    } catch {
      // ignore storage failures
    }
  };

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(matchesScrollKey);
      if (!raw) return;
      const value = Number(raw);
      if (Number.isNaN(value)) return;
      const id = window.setTimeout(() => {
        window.scrollTo({ top: value, left: 0, behavior: 'instant' });
        sessionStorage.removeItem(matchesScrollKey);
      }, 50);
      return () => window.clearTimeout(id);
    } catch {
      // ignore
    }
  }, []);

  const userDocQuery = useQuery({
    queryKey: ["userDoc", user?.uid],
    queryFn: async () => {
      if (!user) return { blocked: [], shortlisted: [] };
      return await fetchUserDoc(user.uid);
    },
    enabled: !!user,
    staleTime: 300000,
    cacheTime: 1800000,
    retry: 1,
  });

  const myProfileQuery = useQuery({
    queryKey: ["myProfile", user?.uid],
    queryFn: async () => {
      if (!user) return null;
      return await fetchMyProfile(user);
    },
    enabled: !!user,
    staleTime: 300000,
    cacheTime: 1800000,
    retry: 1,
  });

  const myProfile = myProfileQuery.data;
  const targetGender = getOppositeGender(myProfile?.gender);
  const isPremium = isPremiumActive(myProfile);

  const matchesTotalQuery = useQuery({
    queryKey: ["matchesTotal", targetGender, user?.uid],
    queryFn: async () => {
      const countQuery = query(
        collection(db, "profiles"),
        where("gender", "==", targetGender),
        where("profileStatus", "==", "Verified")
      );
      const snap = await getCountFromServer(countQuery);
      return snap.data().count || 0;
    },
    enabled: !!targetGender && !!user,
    staleTime: 300000,
    cacheTime: 1800000,
    retry: 1,
  });

  const {
    data: matchesData,
    isLoading: matchesLoading,
    isFetchingNextPage,
    hasNextPage,
    error: matchesError,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["matches", targetGender, blockedIds, user?.uid, isPremium],
    queryFn: fetchMatchesPage,
    enabled: !!targetGender && !!user,
    keepPreviousData: true,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 300000,
    cacheTime: 1800000,
    retry: 1,
  });

  const queryClient = useQueryClient();

  const profiles = useMemo(() => matchesData?.pages.flatMap((page) => page.profiles) ?? [], [matchesData]);

  useEffect(() => {
    if (!profiles || profiles.length === 0) return;
    try {
      profiles.forEach((p) => queryClient.setQueryData(["profile", p.id], p));
    } catch (e) {
      // ignore caching errors
    }
  }, [profiles, queryClient]);

  const loading = userDocQuery.isLoading || myProfileQuery.isLoading || matchesLoading;
  const loadingMore = isFetchingNextPage;
  const hasMore = hasNextPage ?? false;
  const error = userDocQuery.error || myProfileQuery.error || matchesError;

  useEffect(() => {
    if (userDocQuery.data) {
      setBlockedIds(userDocQuery.data.blocked || []);
      setShortlistedIds(userDocQuery.data.shortlisted || []);
    }
  }, [userDocQuery.data]);

  useEffect(() => {
    if (!user || restoredState.current) return;
    const cached = loadMatchesPageState(user.uid);
    restoredState.current = true;
    if (!cached) return;

    setFilters(cached.filters || {
      dobFrom: "", dobTo: "", education: "", profession: "", caste: "",
      heightMin: "", heightMax: "", maritalStatus: "", religion: "",
      income: "", residenceType: "", state: "", district: "", rashi: "",
      familyFinancialStatus: "", collegeTypes: [], withPhotosOnly: false,
    });
    setSearch(cached.search || "");
    setSortOrder(cached.sortOrder === "oldest" ? "oldest" : "newest");
    setShowFilters(cached.showFilters || false);
    setFilterTab(cached.filterTab || "basic");
    setBlockedIds(cached.blockedIds || []);
    setShortlistedIds(cached.shortlistedIds || []);
  }, [user]);

  useEffect(() => {
    if (error) {
      setNotice(error.message || "Could not load matches.");
    } else {
      setNotice("");
    }
  }, [error]);

  useEffect(() => {
    if (!user) return;
    saveMatchesPageState({
      userId: user.uid,
      filters,
      search,
      sortOrder,
      showFilters,
      filterTab,
      blockedIds,
      shortlistedIds,
    });
  }, [user, filters, search, sortOrder, showFilters, filterTab, blockedIds, shortlistedIds]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading || loadingMore || !user) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      fetchNextPage().catch(() => {});
    }, { rootMargin: "300px 0px" });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, user, fetchNextPage]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const isAny = (v) => !v || v.toLowerCase() === "any";
    return profiles.filter((profile) => {
      const birthYear = getBirthYear(profile.dateOfBirth);
      if (filters.dobFrom && birthYear && Number(birthYear) < Number(filters.dobFrom)) return false;
      if (filters.dobTo && birthYear && Number(birthYear) > Number(filters.dobTo)) return false;
      if (!isAny(filters.education) && profile.education !== filters.education) return false;
      if (!isAny(filters.profession) && profile.profession !== filters.profession) return false;
      if (!isAny(filters.caste) && profile.caste !== filters.caste) return false;

      // advanced (premium only)
      if (isPremium) {
        if (!isAny(filters.maritalStatus) && profile.maritalStatus !== filters.maritalStatus) return false;
        if (!isAny(filters.religion) && profile.religion !== filters.religion) return false;
        if (!isAny(filters.income) && profile.annualIncome !== filters.income) return false;
        if (!isAny(filters.residenceType) && profile.residenceType !== filters.residenceType) return false;
        if (!isAny(filters.state) && profile.state !== filters.state) return false;
        if (!isAny(filters.district) && profile.district !== filters.district) return false;
        if (!isAny(filters.rashi) && profile.rashi !== filters.rashi) return false;
        if (!isAny(filters.familyFinancialStatus) && profile.familyFinancialStatus !== filters.familyFinancialStatus) return false;
        if (filters.collegeTypes.length > 0 && !filters.collegeTypes.includes(profile.collegeType)) return false;
        if (filters.withPhotosOnly && (profile.hidePhotos || !profile.imageUrls?.[0])) return false;
        if (filters.heightMin && profile.height && labelToInches(profile.height) < labelToInches(filters.heightMin)) return false;
        if (filters.heightMax && profile.height && labelToInches(profile.height) > labelToInches(filters.heightMax)) return false;
      }

      if (term) {
        const searchable = [profile.fullName, profile.kmProfileId, profile.profession, profile.education, profile.presentResidence, profile.state]
          .filter(Boolean).join(" ").toLowerCase();
        if (!searchable.includes(term)) return false;
      }
      return true;
    });
  }, [filters, profiles, search, isPremium]);

  const sortedFiltered = useMemo(() => {
    // When sortOrder is "newest" (default), Firestore already returns profiles
    // ordered by createdAt desc — do NOT re-sort. Re-sorting across paginated
    // results causes profiles to jump/reorder on each page load.
    // Only sort when user explicitly picks "oldest" and has no filters active
    // (filters reset pagination anyway so reordering is safe).
    if (sortOrder !== "oldest") return filtered;
    return [...filtered].sort((a, b) => compareProfilesBySortOrder(a, b, sortOrder));
  }, [filtered, sortOrder]);

  const educationOptions = useMemo(() => {
    const p = normalizePreference(myProfile?.preferredEducation);
    return p && !partnerEducationOptions.includes(p) ? [...partnerEducationOptions, p] : partnerEducationOptions;
  }, [myProfile?.preferredEducation]);
  const professionOptions = useMemo(() => {
    const p = normalizePreference(myProfile?.preferredProfession);
    return p && !partnerProfessionOptions.includes(p) ? [...partnerProfessionOptions, p] : partnerProfessionOptions;
  }, [myProfile?.preferredProfession]);
  const casteOptions = useMemo(() => {
    const p = normalizePreference(myProfile?.castePreference);
    return p && !partnerCasteOptions.includes(p) ? [...partnerCasteOptions, p] : partnerCasteOptions;
  }, [myProfile?.castePreference]);

  const shortlistProfile = async (profile) => {
    if (!user) return;
    if (shortlistedIds.includes(profile.id)) {
      toast("Already shortlisted");
      return;
    }
    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, { shortlisted: arrayUnion(profile.id), updatedAt: serverTimestamp() });
      setShortlistedIds((prev) => [...prev, profile.id]);
      toast.success("Profile shortlisted");
    } catch (error) {
      toast.error("Could not shortlist");
    }
  };

  const unshortlistProfile = async (profile) => {
    if (!user) return;
    if (shortlistBusyIds.includes(profile.id)) return;
    setShortlistBusyIds((prev) => [...prev, profile.id]);
    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, { shortlisted: arrayRemove(profile.id), updatedAt: serverTimestamp() });
      setShortlistedIds((prev) => prev.filter((id) => id !== profile.id));
      toast.success("Removed from shortlist");
    } catch (error) {
      toast.error("Could not remove from shortlist");
    } finally {
      setShortlistBusyIds((prev) => prev.filter((id) => id !== profile.id));
    }
  };

  const cardActions = (profile, iconOnly = false) => {
    const isAlreadyShortlisted = shortlistedIds.includes(profile.id);
    return (
      <button
        onClick={(event) => {
          if (iconOnly) {
            event.preventDefault();
            event.stopPropagation();
          }
          isAlreadyShortlisted ? setPendingUnshortlist(profile) : shortlistProfile(profile);
        }}
        disabled={shortlistBusyIds.includes(profile.id)}
        aria-label={isAlreadyShortlisted ? "Remove from shortlist" : "Add to shortlist"}
        title={isAlreadyShortlisted ? "Remove from shortlist" : "Add to shortlist"}
        className={iconOnly
          ? `inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/80 bg-white/95 text-brand-600 shadow-soft transition hover:scale-105 hover:bg-white disabled:opacity-60 ${isAlreadyShortlisted ? "text-rose-600" : ""}`
          : `inline-flex w-full items-center justify-center gap-1.5 rounded-2xl px-3.5 py-2.5 text-[12.5px] font-semibold transition ${
          isAlreadyShortlisted
            ? "border border-rose-200 bg-white text-rose-600 hover:bg-rose-50"
            : "bg-brand-gradient text-white hover:shadow-soft active:scale-[0.98]"
        }`}
      >
        {iconOnly ? (
          <Heart size={17} className={isAlreadyShortlisted ? "fill-current" : ""} />
        ) : (
          <><Bookmark size={14} /> {isAlreadyShortlisted ? "Unshortlist" : "Shortlist"}</>
        )}
      </button>
    );
  };

  const confirmUnshortlist = async () => {
    if (!pendingUnshortlist) return;
    await unshortlistProfile(pendingUnshortlist);
    setPendingUnshortlist(null);
  };

  const activeFilterCount = (() => {
    const basicKeys = ["dobFrom", "dobTo", "education", "profession", "caste"];
    const advKeys = ["heightMin", "heightMax", "maritalStatus", "religion", "income", "residenceType", "state", "district", "rashi", "familyFinancialStatus"];
    let count = basicKeys.filter((k) => filters[k]).length;
    if (isPremium) {
      count += advKeys.filter((k) => filters[k]).length;
      if (filters.collegeTypes.length > 0) count += 1;
      if (filters.withPhotosOnly) count += 1;
    }
    return count;
  })();

  const profileCountLabel = useMemo(() => {
    const loaded = profiles.length;
    const visible = sortedFiltered.length;
    const serverTotal = matchesTotalQuery.data;
    const hasFilters = Boolean(search.trim()) || activeFilterCount > 0;

    if (loading) return "Loading profiles...";

    if (loaded === 0) {
      return "No profiles to show";
    }

    if (hasFilters) {
      if (visible === 0) {
        if (hasMore) return "No matches yet · scroll to load more profiles";
        return "No matches found · try different filters";
      }
      if (!hasMore) {
        return visible === 1 ? "1 match found" : `${visible} matches found`;
      }
      return visible === 1
        ? "1 match found · scroll to load more profiles"
        : `${visible} matches found · scroll to load more profiles`;
    }

    if (!hasMore) {
      return loaded === 1 ? "1 profile · all loaded" : `${loaded} profiles · all loaded`;
    }

    const total = serverTotal != null ? Math.max(serverTotal, loaded) : null;
    return total != null
      ? `Showing ${loaded} of ${total} · scroll for more`
      : `Showing ${loaded} · scroll for more`;
  }, [
    profiles.length,
    sortedFiltered.length,
    matchesTotalQuery.data,
    search,
    activeFilterCount,
    loading,
    hasMore,
  ]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <ConfirmDialog
        open={!!pendingUnshortlist}
        title="Remove from shortlist?"
        description={pendingUnshortlist ? `${pendingUnshortlist.fullName} will be removed from your saved list.` : ""}
        Icon={Bookmark}
        tone="warning"
        confirmLabel="Yes, remove"
        loading={shortlistBusyIds.includes(pendingUnshortlist?.id)}
        onClose={() => !shortlistBusyIds.includes(pendingUnshortlist?.id) && setPendingUnshortlist(null)}
        onConfirm={confirmUnshortlist}
      />

      <main className="page-container pt-4 pb-app-nav sm:pt-6 lg:pt-8">
        {!isVerifiedProfile(myProfile) && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900"
          >
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 shrink-0" size={18} />
              <div>
                <h2 className="font-display text-[15px] font-bold">Profile under review</h2>
                <p className="mt-0.5 text-[12.5px] leading-5">
                  Your profile is being reviewed. Once approved, you'll be able to send and receive interests.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Always show matches (even for unverified — they just can't send interests) */}
            {/* Header */}
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
              <h1 className="font-display text-2xl font-bold text-ink-800 sm:text-3xl">
                Discover matches
              </h1>
              <p className="mt-0.5 text-[12.5px] text-ink-400">
                {profileCountLabel}
              </p>

              {/* Search + toolbar */}
              <div className="mt-3 space-y-2.5">
                <div className="input-group h-11 w-full">
                  <Search size={16} className="text-ink-400" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name, ID, profession..."
                    className="text-[13px]"
                  />
                  {search && (
                    <button onClick={() => setSearch("")} className="text-ink-400 hover:text-brand-600">
                      <X size={14} />
                    </button>
                  )}
                </div>

                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-400 sm:text-[11px]">
                      <ArrowUpDown size={12} />
                      Sort
                    </span>
                    <div className="inline-flex h-10 rounded-xl border border-sand-200 bg-white p-0.5">
                      {[
                        { key: "newest", label: "Newest", labelLg: "Newest first" },
                        { key: "oldest", label: "Oldest", labelLg: "Oldest first" },
                      ].map(({ key, label, labelLg }) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setSortOrder(key)}
                          className={`inline-flex h-full items-center justify-center whitespace-nowrap rounded-lg px-2.5 text-[11px] font-semibold transition sm:px-3.5 sm:text-[12.5px] ${
                            sortOrder === key
                              ? "bg-brand-gradient text-white shadow-soft"
                              : "text-ink-500 hover:text-brand-600"
                          }`}
                        >
                          <span className="sm:hidden">{label}</span>
                          <span className="hidden sm:inline">{labelLg}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowFilters(true)}
                      className={`relative inline-flex h-10 w-10 items-center justify-center rounded-xl border transition ${
                        activeFilterCount > 0
                          ? "border-brand-600 bg-brand-50 text-brand-700"
                          : "border-sand-200 bg-white text-ink-500 hover:border-brand-600 hover:text-brand-600"
                      }`}
                      aria-label="Open filters"
                    >
                      <Filter size={16} />
                      {activeFilterCount > 0 && (
                        <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-600 px-1 text-[9px] font-bold text-white">
                          {activeFilterCount}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Filter drawer */}
            <AnimatePresence>
              {showFilters && (
                <>
                  <motion.button
                    type="button"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setShowFilters(false)}
                    aria-label="Close filters"
                    className="fixed inset-0 z-40 bg-ink-900/45 backdrop-blur-sm"
                  />
                  <motion.aside
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "100%" }}
                    transition={{ type: "spring", stiffness: 280, damping: 32 }}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="matches-filter-title"
                    className="fixed bottom-0 left-0 right-0 z-50 mx-auto flex max-h-[90dvh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[26px] border border-b-0 border-sand-200 bg-white shadow-2xl sm:rounded-t-[30px]"
                  >
                    <div className="shrink-0 border-b border-sand-200 bg-white px-4 pb-4 pt-3 sm:px-6">
                      <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-sand-300" />
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <h2 id="matches-filter-title" className="font-display text-xl font-bold text-ink-800 sm:text-2xl">Find your match</h2>
                          <p className="mt-1 text-[12px] text-ink-400 sm:text-[13px]">
                            Set your preferences to refine profiles
                          </p>
                        </div>
                        <button type="button" onClick={() => setShowFilters(false)} className="btn-icon h-10 w-10 shrink-0">
                          <X size={18} />
                        </button>
                      </div>
                    </div>

                    {/* Tabs */}
                    <div className="shrink-0 bg-white px-4 pt-4 sm:px-6">
                      <div role="tablist" aria-label="Filter categories" className="flex items-center gap-1 rounded-2xl bg-sand-100 p-1">
                        {[
                          { key: "basic", label: "Basic" },
                          { key: "advanced", label: "Advanced", premium: true },
                        ].map((t) => {
                          const active = filterTab === t.key;
                          return (
                            <button
                              key={t.key}
                              type="button"
                              onClick={() => setFilterTab(t.key)}
                              role="tab"
                              aria-selected={active}
                              className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl px-4 py-3 text-[13px] font-semibold transition ${
                                active
                                  ? "bg-white text-brand-700 shadow-soft"
                                  : "text-ink-500 hover:text-brand-600"
                              }`}
                            >
                              {t.label}
                              {t.premium && !isPremium && (
                                <Crown size={11} className={active ? "text-amber-200" : "text-amber-500"} />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-sand-50/70 px-4 py-5 sm:px-6">
                      {filterTab === "basic" ? (
                        <BasicFilters
                          filters={filters}
                          setFilters={setFilters}
                          educationOptions={educationOptions}
                          professionOptions={professionOptions}
                          casteOptions={casteOptions}
                        />
                      ) : !isPremium ? (
                        <div className="mx-auto w-full max-w-2xl space-y-4">
                          <div>
                            <h3 className="font-display text-base font-bold text-ink-800">More ways to find the right match</h3>
                            <p className="mt-1 text-[12px] leading-5 text-ink-500">
                              Premium gives you extra control over the profiles you discover.
                            </p>
                          </div>

                          <div className="grid grid-cols-2 gap-2.5">
                            {[
                              ["Height", "Set a preferred range"],
                              ["Marital status", "Choose life experience"],
                              ["Location", "Filter by state & district"],
                              ["Religion & rashi", "Find shared traditions"],
                              ["Income & family", "Set family preferences"],
                              ["College type", "Choose education background"],
                            ].map(([title, description]) => (
                              <div key={title} className="flex min-h-[78px] items-start gap-2.5 rounded-2xl border border-sand-200 bg-white p-3 shadow-sm">
                                <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                                  <Lock size={13} />
                                </span>
                                <span className="min-w-0">
                                  <span className="block truncate text-[12px] font-semibold text-ink-700">{title}</span>
                                  <span className="mt-0.5 block text-[10.5px] leading-4 text-ink-400">{description}</span>
                                </span>
                              </div>
                            ))}
                          </div>

                          <div className="flex flex-col gap-3 rounded-2xl border border-amber-100 bg-amber-50/70 p-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex min-w-0 items-center gap-3">
                              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-amber-600 shadow-sm">
                                <Crown size={18} />
                              </span>
                              <div className="min-w-0">
                                <p className="text-[13px] font-bold text-ink-800">Make your search more personal</p>
                                <p className="mt-0.5 text-[11px] text-ink-500">Explore premium membership for advanced filters.</p>
                              </div>
                            </div>
                            <Link to="/settings/membership" className="btn-primary w-full shrink-0 px-4 py-2.5 text-[12px] sm:w-auto">
                              View plans <ChevronRight size={14} />
                            </Link>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-5">
                          <div>
                            <h3 className="font-display text-base font-bold text-ink-800">Advanced preferences</h3>
                            <p className="mt-1 text-[12px] text-ink-400">Narrow your search with more profile details.</p>
                          </div>
                          <AdvancedFilters
                            filters={filters}
                            setFilters={setFilters}
                          />
                        </div>
                      )}
                    </div>

                    <div className="grid shrink-0 grid-cols-2 gap-3 border-t border-sand-200 bg-white px-4 py-3 sm:px-6"
                      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setFilters({
                            dobFrom: myProfile?.preferredBirthYearFrom || "",
                            dobTo: myProfile?.preferredBirthYearTo || "",
                            education: normalizePreference(myProfile?.preferredEducation),
                            profession: normalizePreference(myProfile?.preferredProfession),
                            caste: normalizePreference(myProfile?.castePreference),
                            heightMin: "", heightMax: "", maritalStatus: "",
                            religion: "", income: "", residenceType: "",
                            state: "", district: "", rashi: "",
                            familyFinancialStatus: "",
                            collegeTypes: [],
                            withPhotosOnly: false,
                          });
                          toast.success("Filters reset");
                        }}
                        className="btn-ghost"
                      >
                        <RefreshCw size={14} /> Reset
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowFilters(false);
                          toast.success("Filters applied");
                        }}
                        className="btn-primary"
                      >
                        Apply <ChevronRight size={15} />
                      </button>
                    </div>
                  </motion.aside>
                </>
              )}
            </AnimatePresence>

            {notice && (
              <motion.p
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 rounded-2xl bg-emerald-50 px-4 py-3 text-[13px] font-semibold text-emerald-700"
              >
                {notice}
              </motion.p>
            )}

            {loading ? (
              <section className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <ProfileCardGridSkeleton key={i} />
                ))}
              </section>
            ) : filtered.length === 0 ? (
              <EmptyState
                Icon={Search}
                title="No matches found"
                description="Try adjusting your filters to discover more profiles."
                action={<button onClick={() => setShowFilters(true)} className="btn-primary px-5">Update filters</button>}
              />
            ) : (
              <section
                className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4"
              >
                {sortedFiltered.map((profile) => (
                  <ProfileCard
                    key={profile.id}
                    profile={profile}
                    variant="grid"
                    actions={cardActions(profile, true)}
                    matchesStyle
                    overlayActions
                    onClick={saveMatchesScroll}
                  />
                ))}
              </section>
            )}

            <div ref={sentinelRef} className="h-8" />

            {loadingMore && (
              <div className="mt-4 text-center text-[12px] font-semibold text-brand-600">Loading more matches...</div>
            )}
      </main>

      <InfoModal
        open={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        Icon={ShieldCheck}
        tone="warning"
        title="Verification required"
        description="Your profile needs to be verified by admin before you can send interests. Please wait for verification. This usually takes 24-48 hours."
        okLabel="OK"
      />
    </div>
  );
};

export default Matches;
